import clsx from "clsx";
import { useNavigate } from "react-router";
import { appPaths } from "../Routes";
import { usePaginatedActivityData } from "../api/activity-data";
import { ActivityData, ActivityDataOrderBy } from "../api/models/ActivityData";
import ActivityDataFilter from "../components/ActivityDataFilter/ActivityDataFilter";
import ContentContainer from "../components/ContentContainer";
import { useDateRangeFilterDropdown } from "../components/DateRangeFilterDropdown/useDateRangeFilterDropdown";
import MainLayout from "../components/MainLayout";
import Pagination from "../components/Pagination";
import Separator from "../components/Separator";
import Table from "../components/Table";
import Typography from "../components/Typography";
import { formatNumber } from "../utils/formatNumber";
import { format } from "date-fns";

const ActivityDataRow = (rowData: ActivityData) => {
    return (
        <>
            <td className={clsx("!min-w-[140px]")}>{rowData.dataSourceName}</td>
            <td className={clsx("!min-w-[58px]")}>
                {format(new Date(rowData.activityStartDate), "dd/MM/yyyy")} -{" "}
                {format(new Date(rowData.activityEndDate), "dd/MM/yyyy")}
            </td>
            <td className={clsx("!min-w-[50px]")}>{rowData.scope}</td>
            <td className={clsx("!min-w-[140px]")}>{rowData.locationName}</td>
            <td className={clsx("!min-w-[110px]")}>
                {formatNumber(rowData.rawActivityQuantity)}{" "}
                <span
                    dangerouslySetInnerHTML={{
                        __html: rowData.rawUnitsSuffixHtml,
                    }}
                />
            </td>
            <td className={clsx("!min-w-[130px]")}>
                {rowData.co2e ? `${formatNumber(rowData.co2e)}kg` : "-"}
            </td>
        </>
    );
};

const ActivityDataPage = () => {
    const navigate = useNavigate();
    const { value, startDate, endDate, handleChange } =
        useDateRangeFilterDropdown();
    const {
        activityDataList,
        isLoading: isLoadingDataSources,
        sortOrder,
        orderBy,
        setOrderBy,
        setSortOrder,
        ...paginationProps
    } = usePaginatedActivityData(startDate, endDate);

    const navigateToActivityDataDetail = (id: number) => {
        navigate(appPaths.activityDataPoint(id.toString()));
    };

    const handleOnOrderBy = (newOrderBy: string) => {
        if (newOrderBy === orderBy) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(newOrderBy as ActivityDataOrderBy);
            setSortOrder("asc");
        }
    };

    return (
        <MainLayout>
            <div className="py-8 px-5">
                <Typography
                    variant="display-small"
                    component="h1"
                    className="text-deep-ocean-20"
                >
                    Asset Data
                </Typography>
            </div>
            <Separator />
            <ActivityDataFilter
                {...paginationProps}
                dateRange={value}
                setDateRange={handleChange}
            />
            {isLoadingDataSources ? (
                <ContentContainer className="py-6">
                    <div className="flex-1 flex justify-center items-center">
                        <div className="flex flex-col items-center">
                            <p className="mt-2 opacity-60">Loading...</p>
                        </div>
                    </div>
                </ContentContainer>
            ) : (
                <>
                    {activityDataList.length === 0 ? (
                        <ContentContainer className="py-6">
                            <div className="flex-1 flex justify-center items-center">
                                <div className="flex flex-col items-center">
                                    <p className="mt-2 opacity-60">
                                        No activity data found matching filter
                                        criteria
                                    </p>
                                </div>
                            </div>
                        </ContentContainer>
                    ) : (
                        <div className="p-5">
                            <Table
                                className="!min-w-[1200px]"
                                headings={[
                                    {
                                        label: "Asset",
                                        orderByKey: "DataSourceName",
                                    },
                                    {
                                        label: "Activity Period",
                                        orderByKey: "ActivityDate",
                                    },
                                    { label: "Scope", orderByKey: "Scope" },
                                    {
                                        label: "Location",
                                        orderByKey: "LocationName",
                                    },
                                    { label: "Raw Quantity" },
                                    { label: "CO2 Equivalent" },
                                ]}
                                data={activityDataList}
                                renderRowCells={(rowData) => (
                                    <ActivityDataRow {...rowData} />
                                )}
                                onRowClick={(rowData) =>
                                    navigateToActivityDataDetail(rowData.id)
                                }
                                sortOrder={sortOrder}
                                orderBy={orderBy}
                                onOrderBy={handleOnOrderBy}
                            />
                            <Pagination {...paginationProps} />
                        </div>
                    )}
                </>
            )}
        </MainLayout>
    );
};

export default ActivityDataPage;
